import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle, Mail, MapPin } from "lucide-react";
import { useT } from "@/i18n/helpers";
import { FadeUp } from "@/components/Reveal";
import Seo from "@/components/Seo";

/* ------------------------------------------------------------------ */
/*  Pages locales — Ziguinchor (Sénégal) & Canchungo (Guinée-Bissau).  */
/*  Uniquement les activités réellement proposées dans chaque zone.    */
/* ------------------------------------------------------------------ */

export default function LocalCity({ city }) {
 const tt = useT();
 const isZiguinchor = city === "ziguinchor";

 const cfg = isZiguinchor
 ? {
  name: "Ziguinchor",
  overline: tt("Implantation · Sénégal", "Location · Senegal", "Implantação · Senegal"),
  h1: tt("NANDA SÁ à Ziguinchor.", "NANDA SÁ in Ziguinchor.", "NANDA SÁ em Ziguinchor."),
  intro: tt(
   "Notre équipe de Ziguinchor, en Casamance, accompagne vos projets au Sénégal : énergie solaire, équipements professionnels, produits du terroir, académie et conseil aux entreprises.",
   "Our Ziguinchor team, in Casamance, supports your projects in Senegal: solar energy, professional equipment, terroir products, academy and business consulting.",
   "A nossa equipa de Ziguinchor, na Casamança, acompanha os seus projectos no Senegal: energia solar, equipamentos profissionais, produtos do terroir, academia e consultoria a empresas."
  ),
  activitiesTitle: tt("Nos activités à Ziguinchor", "Our activities in Ziguinchor", "As nossas actividades em Ziguinchor"),
  activities: [
   {
    name: "NANDA SÁ PRO",
    desc: tt(
     "Installation solaire photovoltaïque, réfrigération et froid professionnel, quincaillerie professionnelle et soudure. Catalogues quincaillerie et soudure en préparation.",
     "Photovoltaic solar installation, professional refrigeration and cooling, professional hardware and welding. Hardware and welding catalogues in preparation.",
     "Instalação solar fotovoltaica, refrigeração e frio profissional, ferragens profissionais e soldadura. Catálogos de ferragens e soldadura em preparação."
    ),
    to: "/marques/energia-pro",
    testId: "ziguinchor-activity-pro",
   },
   {
    name: tt("Produits & Terroirs", "Products & Terroirs", "Produtos & Terroirs"),
    desc: tt(
     "Fleur de sel d'Adéane et miel du Sénégal, récoltés avec soin en Casamance.",
     "Adéane fleur de sel and Senegal honey, carefully harvested in Casamance.",
     "Flor de sal de Adéane e mel do Senegal, colhidos com cuidado na Casamança."
    ),
    to: "/terroirs",
    testId: "ziguinchor-activity-terroirs",
   },
   {
    name: "NANDA SÁ Académie",
    desc: tt(
     "Guides, ressources, programmes et ateliers pour les écoles, les entreprises et les porteurs de projets.",
     "Guides, resources, programmes and workshops for schools, businesses and project leaders.",
     "Guias, recursos, programas e ateliers para escolas, empresas e promotores de projectos."
    ),
    to: "/marques/academie",
    testId: "ziguinchor-activity-academie",
   },
   {
    name: tt("Conseil & implantation", "Consulting & establishment", "Consultoria & implantação"),
    desc: tt(
     "Accompagnement des entreprises et des projets, au Sénégal et en Guinée-Bissau.",
     "Support for businesses and projects, in Senegal and Guinea-Bissau.",
     "Acompanhamento de empresas e projectos, no Senegal e na Guiné-Bissau."
    ),
    to: "/conseil-implantation",
    testId: "ziguinchor-activity-conseil",
   },
  ],
  address: tt("Ziguinchor, Sénégal", "Ziguinchor, Senegal", "Ziguinchor, Senegal"),
  phone: "+221 71 009 04 10",
  wa: "221710090410",
  other: { to: "/canchungo", label: tt("NANDA SÁ à Canchungo", "NANDA SÁ in Canchungo", "NANDA SÁ em Canchungo") },
 }
 : {
  name: "Canchungo",
  overline: tt("Implantation · Guinée-Bissau", "Location · Guinea-Bissau", "Implantação · Guiné-Bissau"),
  h1: tt("NANDA SÁ à Canchungo.", "NANDA SÁ in Canchungo.", "NANDA SÁ em Canchungo."),
  intro: tt(
   "Notre équipe de Canchungo, dans la région de Cacheu, accompagne vos projets en Guinée-Bissau : énergie solaire, cosmétiques naturels, académie et conseil aux entreprises.",
   "Our Canchungo team, in the Cacheu region, supports your projects in Guinea-Bissau: solar energy, natural cosmetics, academy and business consulting.",
   "A nossa equipa de Canchungo, na região de Cacheu, acompanha os seus projectos na Guiné-Bissau: energia solar, cosméticos naturais, academia e consultoria a empresas."
  ),
  activitiesTitle: tt("Nos activités à Canchungo", "Our activities in Canchungo", "As nossas actividades em Canchungo"),
  activities: [
   {
    name: "NANDA SÁ PRO",
    desc: tt(
     "Installation solaire photovoltaïque, réfrigération et froid professionnel, quincaillerie professionnelle et soudure. Catalogues quincaillerie et soudure en préparation.",
     "Photovoltaic solar installation, professional refrigeration and cooling, professional hardware and welding. Hardware and welding catalogues in preparation.",
     "Instalação solar fotovoltaica, refrigeração e frio profissional, ferragens profissionais e soldadura. Catálogos de ferragens e soldadura em preparação."
    ),
    to: "/marques/energia-pro",
    testId: "canchungo-activity-pro",
   },
   {
    name: tt("Cosmétiques naturels", "Natural cosmetics", "Cosméticos naturais"),
    desc: tt(
     "Chantillys de karité, savons naturels et huiles de soin, fabriqués en Guinée-Bissau.",
     "Whipped shea butters, natural soaps and care oils, made in Guinea-Bissau.",
     "Chantillys de karité, sabonetes naturais e óleos de cuidado, fabricados na Guiné-Bissau."
    ),
    to: "/cosmetiques",
    testId: "canchungo-activity-cosmetiques",
   },
   {
    name: "NANDA SÁ Académie",
    desc: tt(
     "Guides, ressources, programmes et ateliers pour les écoles, les entreprises et les porteurs de projets.",
     "Guides, resources, programmes and workshops for schools, businesses and project leaders.",
     "Guias, recursos, programas e ateliers para escolas, empresas e promotores de projectos."
    ),
    to: "/marques/academie",
    testId: "canchungo-activity-academie",
   },
   {
    name: tt("Conseil & implantation", "Consulting & establishment", "Consultoria & implantação"),
    desc: tt(
     "Accompagnement des entreprises et des projets, au Sénégal et en Guinée-Bissau.",
     "Support for businesses and projects, in Senegal and Guinea-Bissau.",
     "Acompanhamento de empresas e projectos, no Senegal e na Guiné-Bissau."
    ),
    to: "/conseil-implantation",
    testId: "canchungo-activity-conseil",
   },
  ],
  address: tt("Canchungo, Région de Cacheu, Guinée-Bissau", "Canchungo, Cacheu Region, Guinea-Bissau", "Canchungo, Região de Cacheu, Guiné-Bissau"),
  phone: "+245 956 828 351",
  wa: "245956828351",
  other: { to: "/ziguinchor", label: tt("NANDA SÁ à Ziguinchor", "NANDA SÁ in Ziguinchor", "NANDA SÁ em Ziguinchor") },
 };

 return (
 <div data-testid={`page-${city}`} className="bg-white">
 <Seo />

 {/* HERO */}
 <section className="border-b border-nanda-line">
 <div className="mx-auto max-w-7xl px-6 sm:px-12 pt-36 md:pt-48 pb-20 md:pb-28">
 <FadeUp>
 <div className="label-overline text-nanda-muted mb-8">{cfg.overline}</div>
 </FadeUp>
 <FadeUp delay={0.1}>
 <h1 className="font-sans font-light tracking-tight text-nanda-ink text-5xl sm:text-6xl md:text-7xl leading-[1.02] text-balance" data-testid={`${city}-h1`}>
 {cfg.h1}
 </h1>
 </FadeUp>
 <FadeUp delay={0.2}>
 <p className="mt-10 max-w-2xl text-nanda-inkSoft text-lg md:text-xl font-light leading-relaxed">
 {cfg.intro}
 </p>
 </FadeUp>
 </div>
 </section>

 {/* ACTIVITÉS */}
 <section className="border-b border-nanda-line">
 <div className="mx-auto max-w-7xl px-6 sm:px-12 py-20 md:py-28">
 <FadeUp className="mb-12">
 <h2 className="font-sans font-light tracking-tight text-nanda-ink text-3xl sm:text-4xl leading-[1.1]">{cfg.activitiesTitle}</h2>
 </FadeUp>
 <div>
 {cfg.activities.map((a, i) => (
 <FadeUp key={a.testId} delay={i * 0.06}>
 <Link
 to={a.to}
 data-testid={a.testId}
 className="group grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8 py-8 md:py-10 border-t border-nanda-line first:border-t-0 items-baseline"
 >
 <div className="md:col-span-4 font-sans font-medium tracking-tight text-xl md:text-2xl text-nanda-ink group-hover:text-nanda-terracotta transition-colors duration-300">
 {a.name}
 </div>
 <div className="md:col-span-7 text-nanda-inkSoft text-base font-light leading-relaxed">{a.desc}</div>
 <div className="md:col-span-1 flex md:justify-end">
 <ArrowUpRight size={18} strokeWidth={1.5} className="text-nanda-muted group-hover:text-nanda-terracotta group-hover:translate-x-1 transition-all duration-300" />
 </div>
 </Link>
 </FadeUp>
 ))}
 </div>
 </div>
 </section>

 {/* CONTACT LOCAL */}
 <section className="border-b border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-7xl px-6 sm:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10">
 <FadeUp className="md:col-span-7">
 <div className="label-overline text-nanda-muted mb-6 inline-flex items-center gap-2"><MapPin size={14} strokeWidth={1.5} /> Contact</div>
 <div className="text-nanda-ink text-2xl font-light" data-testid={`${city}-address`}>{cfg.address}</div>
 <a
 href={`https://wa.me/${cfg.wa}`}
 target="_blank"
 rel="noopener noreferrer"
 data-testid={`${city}-phone`}
 className="mt-4 inline-flex items-center gap-2 text-nanda-ink text-xl font-light hover:text-nanda-terracotta transition-colors"
 >
 <MessageCircle size={16} strokeWidth={1.5} className="text-nanda-muted" /> {cfg.phone}
 </a>
 <div className="mt-3">
 <a href="mailto:hello@nanda-sa.com" className="inline-flex items-center gap-2 text-nanda-inkSoft text-base font-light hover:text-nanda-terracotta transition-colors" data-testid={`${city}-email`}>
 <Mail size={15} strokeWidth={1.5} className="text-nanda-muted" /> hello@nanda-sa.com
 </a>
 </div>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-5 flex flex-col items-start md:items-end justify-center gap-6">
 <Link
 to="/contact"
 data-testid={`${city}-cta-contact`}
 className="inline-flex items-center gap-2.5 bg-nanda-terracotta text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#8F5B3F] transition-colors duration-300"
 >
 {tt("Nous écrire", "Write to us", "Escreva-nos")}
 <ArrowUpRight size={15} strokeWidth={1.5} />
 </Link>
 <Link to={cfg.other.to} data-testid={`${city}-link-other`} className="text-sm font-medium text-nanda-inkSoft hover:text-nanda-ink link-underline transition-colors">
 {tt("Notre autre implantation :", "Our other location:", "A nossa outra implantação:")} {cfg.other.label} →
 </Link>
 </FadeUp>
 </div>
 </section>
 </div>
 );
}
